/**
 * PO Lines Tab Component
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the usePOLinesState hook.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { useIsCustomer } from '../../../hooks/usePortalMode';
import POLineDetailModal from '../POLineDetailModal';
import { AcceptDateModal, DeclineDateModal, ReasonCodeSelect, getEffectiveReason } from '../AcknowledgmentWorkflowModals';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { SectionTourTrigger } from '../../tour/SectionTourTrigger';

// Import modular components
import {
  usePOLinesState,
  POLinesFiltersPanel,
  POLinesTable,
  PutOnHoldModal,
  ReleaseHoldModal,
  PurchaseOrderLine,
} from './po-lines';

interface POLinesTabProps {
  initialLineId?: number | null;
  initialLineTab?: string;  // CR-3929: Auto-open specific tab in line detail (e.g., 'queries')
}

export default function POLinesTab({ initialLineId, initialLineTab }: POLinesTabProps = {}) {
  const isCustomer = useIsCustomer();
  const { formatDate, formatCurrency } = useFormatters();

  const {
    // Data
    lines,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    needsActionCount,
    openCount,
    closedCount,
    onHoldCount,
    completedCount,
    dueTodayCount,
    highPriorityCount,
    pageSize,
    activeTab,
    showFilters,
    user,

    // Selection
    selectedLineId,
    selectedLineAction,
    selectedLineForHold,
    selectedLineForWorkflow,
    selectedLineIds,
    bulkAccepting,
    bulkRejecting,
    bulkHolding,
    showBulkAcceptModal,
    showBulkRejectModal,
    showBulkHoldModal,

    // Modals
    putOnHoldModalOpen,
    releaseHoldModalOpen,
    acceptModalOpen,
    declineModalOpen,
    holdReason,

    // Sort
    sortField,
    sortDirection,

    // Export
    exporting,

    // Filters
    filters,
    filterOptions,

    // Columns
    columns,
    showColumnDropdown,
    columnDropdownRef,

    // Setters
    setActiveTab,
    setShowFilters,
    setSelectedLineId,
    setSelectedLineAction,
    setSelectedLineForWorkflow,
    setSelectedLineIds,
    setPutOnHoldModalOpen,
    setReleaseHoldModalOpen,
    setAcceptModalOpen,
    setDeclineModalOpen,
    setHoldReason,
    setShowColumnDropdown,

    // Handlers
    fetchLines,
    patchLine,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearch,
    clearFilters,
    handleSort,
    toggleColumn,
    resetColumns,
    selectAllColumns,
    reorderColumns,
    handlePutOnHold,
    handleReleaseHold,
    submitPutOnHold,
    submitReleaseHold,
    handleToggleCritical,
    handleWorkflowAction,
    handleSelectAll,
    handleToggleSelect,
    handleBulkAccept,
    handleBulkReject,
    handleBulkPutOnHold,
    setShowBulkAcceptModal,
    setShowBulkRejectModal,
    setShowBulkHoldModal,
    bulkResultLog,
    showBulkResultLog,
    setShowBulkResultLog,
    handleExport,
    handleTemplateExport,
    exportTemplates,
    shouldShowActions,
    shouldShowDecline,
    shouldShowProposeChange,
    getNextAction,
    getSelectableLines,
    canSelectForBulkAccept,
  } = usePOLinesState();

  // CR-4728: Track which tab to open in the PO Line detail modal
  // (e.g., 'change-requests' when clicking the Review Changes icon)
  const [selectedLineTab, setSelectedLineTab] = useState<string | undefined>(undefined);

  // CR-3929: Auto-open PO line detail from notification deep link
  useEffect(() => {
    if (initialLineId) {
      setSelectedLineId(initialLineId);
    }
  }, [initialLineId]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading PO lines...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={() => fetchLines()}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle workflow modal opening
  // CR-4647: Customer accept skips the modal — accepts immediately.
  const handleSelectLineForWorkflow = async (line: PurchaseOrderLine, modal: 'accept' | 'decline') => {
    const isCustomerView = String(user?.platform) === 'CUSTOMER' || line.acknowledge_status === 'new';
    if (modal === 'accept' && isCustomerView) {
      try {
        await handleWorkflowAction({ action: 'accept', lineId: line.id });
      } catch {
        // error already toasted by handleWorkflowAction
      }
      return;
    }
    setSelectedLineForWorkflow(line);
    if (modal === 'accept') {
      setAcceptModalOpen(true);
    } else {
      setDeclineModalOpen(true);
    }
  };

  // Handle propose change action
  const handleProposeChange = (line: PurchaseOrderLine) => {
    setSelectedLineAction('propose_change');
    setSelectedLineId(line.id);
  };

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end">
        <SectionTourTrigger
          pageKey="purchasing"
          sectionKey="po_lines"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <POLinesFiltersPanel
          filters={filters}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClearFilters={clearFilters}
          onHideFilters={() => setShowFilters(false)}
        />
      )}

      {/* Lines Table */}
      <POLinesTable
        lines={lines}
        totalCount={totalCount}
        needsActionCount={needsActionCount}
        openCount={openCount}
        closedCount={closedCount}
        onHoldCount={onHoldCount}
        completedCount={completedCount}
        dueTodayCount={dueTodayCount}
        highPriorityCount={highPriorityCount}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        activeTab={activeTab}
        showFilters={showFilters}
        exporting={exporting}
        user={user}
        columns={columns}
        showColumnDropdown={showColumnDropdown}
        columnDropdownRef={columnDropdownRef}
        sortField={sortField}
        sortDirection={sortDirection}
        selectedLineIds={selectedLineIds}
        bulkAccepting={bulkAccepting}
        bulkRejecting={bulkRejecting}
        formatDate={formatDate}
        formatCurrency={formatCurrency}
        onSetActiveTab={setActiveTab}
        onSetShowFilters={setShowFilters}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        onExport={handleExport}
        onTemplateExport={handleTemplateExport}
        exportTemplates={exportTemplates}
        onToggleColumn={toggleColumn}
        onResetColumns={resetColumns}
        onSelectAllColumns={selectAllColumns}
        onReorderColumns={reorderColumns}
        onSetShowColumnDropdown={setShowColumnDropdown}
        onClearFilters={clearFilters}
        onSelectLine={setSelectedLineId}
        onReviewChanges={(lineId: number) => { setSelectedLineId(lineId); setSelectedLineTab('change-requests'); }}
        onShowJobStatus={(lineId: number) => { setSelectedLineId(lineId); setSelectedLineTab('job-status'); }}
        onSelectLineForWorkflow={handleSelectLineForWorkflow}
        onProposeChange={handleProposeChange}
        onToggleCritical={handleToggleCritical}
        onPutOnHold={handlePutOnHold}
        onReleaseHold={handleReleaseHold}
        onSelectAll={handleSelectAll}
        onToggleSelect={handleToggleSelect}
        onBulkAccept={() => setShowBulkAcceptModal(true)}
        onBulkReject={() => setShowBulkRejectModal(true)}
        onBulkHold={() => setShowBulkHoldModal(true)}
        bulkHolding={bulkHolding}
        onSetSelectedLineIds={setSelectedLineIds}
        shouldShowActions={shouldShowActions}
        shouldShowDecline={shouldShowDecline}
        shouldShowProposeChange={shouldShowProposeChange}
        getNextAction={getNextAction}
        getSelectableLines={getSelectableLines}
        canSelectForBulkAccept={canSelectForBulkAccept}
        filters={filters}
      />

      {/* PO Line Detail Modal */}
      {selectedLineId && (
        <POLineDetailModal
          key={selectedLineId}
          lineId={selectedLineId}
          isOpen={selectedLineId !== null}
          onClose={() => {
            setSelectedLineId(null);
            setSelectedLineAction(null);
            setSelectedLineTab(undefined);
          }}
          userType={isCustomer ? 'customer' : 'supplier'}
          initialAction={selectedLineAction}
          initialTab={selectedLineTab || (selectedLineId === initialLineId ? initialLineTab : undefined)}
          onUpdate={() => patchLine(selectedLineId)}
        />
      )}

      {/* Put On Hold Modal */}
      <PutOnHoldModal
        isOpen={putOnHoldModalOpen}
        line={selectedLineForHold}
        holdReason={holdReason}
        onHoldReasonChange={setHoldReason}
        onClose={() => setPutOnHoldModalOpen(false)}
        onSubmit={submitPutOnHold}
      />

      {/* Release Hold Modal */}
      <ReleaseHoldModal
        isOpen={releaseHoldModalOpen}
        line={selectedLineForHold}
        onClose={() => setReleaseHoldModalOpen(false)}
        onSubmit={submitReleaseHold}
      />

      {/* Bulk Accept Confirmation Modal */}
      <Dialog open={showBulkAcceptModal} onOpenChange={setShowBulkAcceptModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Selected Lines</DialogTitle>
            <DialogDescription className="sr-only">
              Confirm bulk acceptance of selected PO lines
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to accept {selectedLineIds.size} selected line{selectedLineIds.size > 1 ? 's' : ''}?
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkAcceptModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowBulkAcceptModal(false);
                handleBulkAccept();
              }}
              disabled={bulkAccepting}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {bulkAccepting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                `Accept ${selectedLineIds.size} Line${selectedLineIds.size > 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reject Confirmation Modal with Reason Selection */}
      <BulkRejectModal
        open={showBulkRejectModal}
        onOpenChange={setShowBulkRejectModal}
        count={selectedLineIds.size}
        rejecting={bulkRejecting}
        onReject={(reason) => handleBulkReject(reason)}
      />

      {/* Bulk Put On Hold Modal */}
      <BulkHoldModal
        open={showBulkHoldModal}
        onOpenChange={setShowBulkHoldModal}
        count={selectedLineIds.size}
        holding={bulkHolding}
        onHold={(reason) => handleBulkPutOnHold(reason)}
      />

      {/* Bulk Operation Failure Log */}
      <Dialog open={showBulkResultLog} onOpenChange={setShowBulkResultLog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Operation — Failed Lines</DialogTitle>
            <DialogDescription>
              {bulkResultLog.length} line{bulkResultLog.length !== 1 ? 's' : ''} could not be processed. Failed lines remain selected in the table.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b">
                  <th className="text-left px-3 py-2 font-medium">SO Number</th>
                  <th className="text-left px-3 py-2 font-medium">Line</th>
                  <th className="text-left px-3 py-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {bulkResultLog.map((entry) => (
                  <tr key={entry.lineId} className="border-b last:border-0">
                    <td className="px-3 py-2">{entry.poNumber}</td>
                    <td className="px-3 py-2">{entry.lineNumber}</td>
                    <td className="px-3 py-2 text-red-600">{entry.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkResultLog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow Modals */}
      {selectedLineForWorkflow && (
        <>
          <AcceptDateModal
            isOpen={acceptModalOpen}
            onClose={() => {
              setAcceptModalOpen(false);
              setSelectedLineForWorkflow(null);
            }}
            lineId={selectedLineForWorkflow.id}
            lineNumber={selectedLineForWorkflow.line_number}
            partNumber={selectedLineForWorkflow.part_number}
            orderNumber={selectedLineForWorkflow.po_number}
            currentDeliveryDate={selectedLineForWorkflow.delivery_date}
            onSubmit={handleWorkflowAction}
          />
          <DeclineDateModal
            isOpen={declineModalOpen}
            onClose={() => {
              setDeclineModalOpen(false);
              setSelectedLineForWorkflow(null);
            }}
            lineId={selectedLineForWorkflow.id}
            lineNumber={selectedLineForWorkflow.line_number}
            partNumber={selectedLineForWorkflow.part_number}
            currentDeliveryDate={selectedLineForWorkflow.delivery_date}
            onSubmit={handleWorkflowAction}
          />
        </>
      )}
    </div>
  );
}

/** Bulk Reject modal with reason code selection */
function BulkRejectModal({
  open,
  onOpenChange,
  count,
  rejecting,
  onReject,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  rejecting: boolean;
  onReject: (reason: string) => void;
}) {
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');

  const effectiveReason = getEffectiveReason(reasonSelect, customReason);

  const handleReject = () => {
    if (!effectiveReason) return;
    onReject(effectiveReason);
  };

  // Reset on close
  useEffect(() => {
    if (!open) {
      setReasonSelect('');
      setCustomReason('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Selected Lines</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm bulk rejection of selected PO lines
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to reject {count} selected line{count > 1 ? 's' : ''}. Please select a reason.
          </p>
          <ReasonCodeSelect
            value={reasonSelect}
            onChange={setReasonSelect}
            customValue={customReason}
            onCustomChange={setCustomReason}
            required
            label="Reason for Rejection"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            disabled={rejecting || !effectiveReason}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {rejecting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rejecting...
              </>
            ) : (
              `Reject ${count} Line${count > 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Bulk Put On Hold modal with reason input */
function BulkHoldModal({
  open,
  onOpenChange,
  count,
  holding,
  onHold,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  holding: boolean;
  onHold: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  const handleHold = () => {
    if (!reason.trim()) return;
    onHold(reason.trim());
  };

  // Reset on close
  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Put Selected Lines On Hold</DialogTitle>
          <DialogDescription className="sr-only">
            Put selected PO lines on hold with a reason
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to put {count} selected line{count > 1 ? 's' : ''} on hold. The same reason will be applied to all lines.
          </p>
          <div className="space-y-2">
            <Label htmlFor="bulk-hold-reason">Hold Reason</Label>
            <Textarea
              id="bulk-hold-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for putting lines on hold..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleHold}
            disabled={holding || !reason.trim()}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {holding ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Putting on hold...
              </>
            ) : (
              `Put ${count} Line${count > 1 ? 's' : ''} On Hold`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
